import {
  completionYMMarks,
  completionYMs,
  dateMax,
  dateMin,
  defaultDspPrice,
  defaultDspTsuboArea,
  estatePriceMax,
  estatePriceMin,
  roomsMarks,
  roomsMax,
  roomsMin,
  squareAreaOfMansionMarks,
  squareAreaOfMansionMax,
  squareAreaOfMansionMin,
  tsuboAreaMarks,
  tsuboAreaMax,
  tsuboAreaMin
} from "common/consts/estates";
import { GroupEnum } from "common/enums/GroupEnum";
import { buildFilterConditionsOfDefaultCondition } from "common/functions/defaultConditions";
import { useAutoReducer } from "common/functions/reducers";
import { BldConditionSelection } from "common/interfaces/BldConditionSelection";
import { City } from "common/interfaces/City";
import { EstateFilterConditions } from "common/interfaces/EstateFilterConditions";
import { EstateType } from "common/interfaces/EstateType";
import { PurposeSummaryB } from "common/interfaces/PurposeSummaryB";
import { StructureSummary } from "common/interfaces/StructureSummary";
import { MainContextContainer } from "components/Main";
import { DefaultCondition } from "graphql/API";
import { useCityHooks } from "hooks/cityHooks";
import { useJsonMasterHooks } from "hooks/jsonMasterHooks";
import { usePrefectureHooks } from "hooks/prefectureHooks";
import { useSliderThemeHooks } from "hooks/sliderThemeHooks";
import { useUserHooks } from "hooks/userHooks";
import {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { specialCities } from "../SwitchAreaDialog/const";
import { DefaultConditionInputDialogProps } from "./interfaces";

export const useDefaultConditionInputDialogHooks = (
  props: DefaultConditionInputDialogProps
) => {
  const initRef = useRef(true);

  const titRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const mainContext = useContext(MainContextContainer);

  const { sliderTheme } = useSliderThemeHooks();

  const [busy, setBusy] = useState(false);

  const [selectedCities, setSelectedCities] = useState<City[]>([]);

  const [citiesErrorMessage, setCitiesErrorMessage] = useState("");

  const [priceLabel, setPriceLabel] = useState<boolean>(defaultDspPrice);
  const [tsuboLabel, setTsuboLabel] = useState<boolean>(defaultDspTsuboArea);

  const { prefectures } = usePrefectureHooks(mainContext.setFatal);

  const areas = useMemo(() => {
    if (
      mainContext.user?.defaultCondition?.areas &&
      mainContext.user.defaultCondition.areas.length > 0
    ) {
      return mainContext.user.defaultCondition.areas;
    }

    return mainContext.company?.areas;
  }, [mainContext.company?.areas, mainContext.user?.defaultCondition?.areas]);

  const areaPrefs = useMemo(() => {
    if (
      mainContext.user?.defaultCondition?.areaPrefs &&
      mainContext.user.defaultCondition.areaPrefs.length > 0
    ) {
      return mainContext.user.defaultCondition.areaPrefs;
    }

    return mainContext.company?.areaPrefs;
  }, [mainContext]);

  const selectedPrefectures = useMemo(() => {
    const cityCodes = [...(areas ?? []), ...(areaPrefs ?? [])];
    const prefs = [
      ...new Set(
        cityCodes?.map((x) => ((x?.length || 0) >= 2 ? x?.substring(0, 2) : ""))
      ).values()
    ].filter((x) => x);

    return prefectures.filter((pref) => prefs.includes(pref.pref_code));
  }, [areas, areaPrefs, prefectures]);

  const { cities } = useCityHooks(selectedPrefectures, mainContext.setFatal);

  const { jsonMasters: estateTypes } = useJsonMasterHooks<EstateType>(
    "estate_types",
    props.setFatal
  );

  const { jsonMasters: bldConditionSelections } =
    useJsonMasterHooks<BldConditionSelection>(
      "bld_condition_selections",
      props.setFatal
    );

  const { jsonMasters: structureSummaries } =
    useJsonMasterHooks<StructureSummary>("structure_summaries", props.setFatal);

  const { jsonMasters: purposeSummaryBs } = useJsonMasterHooks<PurposeSummaryB>(
    "purpose_summary_bs",
    props.setFatal
  );

  const { busy: busyOfUser, saveDefaultCondition: saveDefaultConditionOfUser } =
    useUserHooks(mainContext.userInfo?.username, mainContext.setFatal);

  const selectedPrefecturesOfUser = useMemo(() => {
    const prefs =
      mainContext.defaultConditionOfUser?.areaPrefs !== null &&
      mainContext.defaultConditionOfUser?.areaPrefs !== undefined &&
      mainContext.defaultConditionOfUser.areaPrefs.length > 0
        ? mainContext.defaultConditionOfUser?.areaPrefs
        : (mainContext.company?.areaPrefs ?? []);

    return prefectures.filter((pref) => prefs.includes(pref.pref_code));
  }, [mainContext, prefectures]);

  const selectedCitiesOfUser = useMemo(() => {
    const areas =
      mainContext.defaultConditionOfUser?.areas !== null &&
      mainContext.defaultConditionOfUser?.areas !== undefined &&
      mainContext.defaultConditionOfUser.areas.length > 0
        ? mainContext.defaultConditionOfUser?.areas
        : (mainContext.company?.areas ?? []);

    return cities.filter((city) => areas.includes(city.city_code));
  }, [cities, mainContext]);

  const filterConditionsOfUser = useMemo(() => {
    const newFilterConditions = {
      ...props.defaultFilterConditions
    };

    if ((mainContext.defaultConditionOfUser?.estateTypes ?? []).length > 0) {
      newFilterConditions.estateTypes = (
        mainContext.defaultConditionOfUser?.estateTypes ?? []
      )
        .map((x) => estateTypes.find((m) => m.name === x))
        .filter((x) => x) as EstateType[];
    }

    if (
      (mainContext.defaultConditionOfUser?.bldConditionSelections ?? [])
        .length > 0
    ) {
      newFilterConditions.bldConditionSelections = (
        mainContext.defaultConditionOfUser?.bldConditionSelections ?? []
      )
        .map((x) => bldConditionSelections.find((m) => m.name === x) ?? null)
        .filter((x) => x) as BldConditionSelection[];
    }

    if (
      (mainContext.defaultConditionOfUser?.structureSummaries ?? []).length > 0
    ) {
      newFilterConditions.structureSummaries = (
        mainContext.defaultConditionOfUser?.structureSummaries ?? []
      )
        .map((x) => structureSummaries.find((m) => m.name === x) ?? null)
        .filter((x) => x) as StructureSummary[];
    }

    if (
      (mainContext.defaultConditionOfUser?.purposeSummaryBs ?? []).length > 0
    ) {
      newFilterConditions.purposeSummaryBs = (
        mainContext.defaultConditionOfUser?.purposeSummaryBs ?? []
      )
        .map((x) => purposeSummaryBs.find((m) => m.name === x) ?? null)
        .filter((x) => x) as PurposeSummaryB[];
    }

    if (
      mainContext.defaultConditionOfUser?.companyRecNo !== null &&
      mainContext.defaultConditionOfUser?.companyRecNo !== undefined
    ) {
      newFilterConditions.companyRecNo =
        mainContext.defaultConditionOfUser.companyRecNo;
    }

    if (
      mainContext.defaultConditionOfUser?.tsuboAreaFrom != null &&
      mainContext.defaultConditionOfUser?.tsuboAreaFrom !== undefined
    ) {
      newFilterConditions.tsuboAreaFrom =
        mainContext.defaultConditionOfUser.tsuboAreaFrom;
    }

    if (
      mainContext.defaultConditionOfUser?.tsuboAreaTo != null &&
      mainContext.defaultConditionOfUser?.tsuboAreaTo !== undefined
    ) {
      newFilterConditions.tsuboAreaTo =
        mainContext.defaultConditionOfUser.tsuboAreaTo;
    }

    if (
      mainContext.defaultConditionOfUser?.squareAreaOfMansionFrom != null &&
      mainContext.defaultConditionOfUser?.squareAreaOfMansionFrom !== undefined
    ) {
      newFilterConditions.squareAreaOfMansionFrom =
        mainContext.defaultConditionOfUser.squareAreaOfMansionFrom;
    }

    if (
      mainContext.defaultConditionOfUser?.squareAreaOfMansionTo != null &&
      mainContext.defaultConditionOfUser?.squareAreaOfMansionTo !== undefined
    ) {
      newFilterConditions.squareAreaOfMansionTo =
        mainContext.defaultConditionOfUser.squareAreaOfMansionTo;
    }

    if (
      mainContext.defaultConditionOfUser?.estatePriceFrom != null &&
      mainContext.defaultConditionOfUser?.estatePriceFrom !== undefined
    ) {
      newFilterConditions.estatePriceFrom =
        mainContext.defaultConditionOfUser.estatePriceFrom;
    }

    if (
      mainContext.defaultConditionOfUser?.estatePriceTo != null &&
      mainContext.defaultConditionOfUser?.estatePriceTo !== undefined
    ) {
      newFilterConditions.estatePriceTo =
        mainContext.defaultConditionOfUser.estatePriceTo;
    }

    if (
      mainContext.defaultConditionOfUser?.alsoCreatedAtFrom != null &&
      mainContext.defaultConditionOfUser?.alsoCreatedAtFrom !== undefined
    ) {
      newFilterConditions.alsoCreatedAtFrom =
        mainContext.defaultConditionOfUser.alsoCreatedAtFrom * 1000;
    }

    if (
      mainContext.defaultConditionOfUser?.alsoCreatedAtTo != null &&
      mainContext.defaultConditionOfUser?.alsoCreatedAtTo !== undefined
    ) {
      newFilterConditions.alsoCreatedAtTo =
        mainContext.defaultConditionOfUser.alsoCreatedAtTo * 1000;
    }

    if (
      mainContext.defaultConditionOfUser?.alsoPricedAtFrom != null &&
      mainContext.defaultConditionOfUser?.alsoPricedAtFrom !== undefined
    ) {
      newFilterConditions.alsoPricedAtFrom =
        mainContext.defaultConditionOfUser.alsoPricedAtFrom * 1000;
    }

    if (
      mainContext.defaultConditionOfUser?.alsoPricedAtTo != null &&
      mainContext.defaultConditionOfUser?.alsoPricedAtTo !== undefined
    ) {
      newFilterConditions.alsoPricedAtTo =
        mainContext.defaultConditionOfUser.alsoPricedAtTo * 1000;
    }

    if (
      mainContext.defaultConditionOfUser?.completionYMFrom != null &&
      mainContext.defaultConditionOfUser?.completionYMFrom !== undefined
    ) {
      newFilterConditions.completionYMFrom =
        mainContext.defaultConditionOfUser.completionYMFrom;
    }

    if (
      mainContext.defaultConditionOfUser?.completionYMTo != null &&
      mainContext.defaultConditionOfUser?.completionYMTo !== undefined &&
      mainContext.defaultConditionOfUser?.completionYMTo <= completionYMs[1]
    ) {
      newFilterConditions.completionYMTo =
        mainContext.defaultConditionOfUser.completionYMTo;
    }

    if (
      mainContext.defaultConditionOfUser?.petOk != null &&
      mainContext.defaultConditionOfUser?.petOk !== undefined
    ) {
      newFilterConditions.petOk = mainContext.defaultConditionOfUser.petOk;
    }

    if (
      mainContext.defaultConditionOfUser?.flat != null &&
      mainContext.defaultConditionOfUser?.flat !== undefined
    ) {
      newFilterConditions.flat = mainContext.defaultConditionOfUser.flat;
    }

    if (
      mainContext.defaultConditionOfUser?.forRent != null &&
      mainContext.defaultConditionOfUser?.forRent !== undefined
    ) {
      newFilterConditions.forRent = mainContext.defaultConditionOfUser.forRent;
    }

    if (
      mainContext.defaultConditionOfUser?.exceptForRent != null &&
      mainContext.defaultConditionOfUser?.exceptForRent !== undefined
    ) {
      newFilterConditions.exceptForRent =
        mainContext.defaultConditionOfUser.exceptForRent;
    }

    if (
      mainContext.defaultConditionOfUser?.venderOnly != null &&
      mainContext.defaultConditionOfUser?.venderOnly !== undefined
    ) {
      newFilterConditions.venderOnly =
        mainContext.defaultConditionOfUser.venderOnly;
    }

    if (
      mainContext.defaultConditionOfUser?.roomsFrom != null &&
      mainContext.defaultConditionOfUser?.roomsFrom !== undefined
    ) {
      newFilterConditions.roomsFrom =
        mainContext.defaultConditionOfUser.roomsFrom;
    }

    if (
      mainContext.defaultConditionOfUser?.roomsTo != null &&
      mainContext.defaultConditionOfUser?.roomsTo !== undefined
    ) {
      newFilterConditions.roomsTo = mainContext.defaultConditionOfUser.roomsTo;
    }

    return newFilterConditions;
  }, [
    bldConditionSelections,
    estateTypes,
    mainContext,
    props.defaultFilterConditions,
    purposeSummaryBs,
    structureSummaries
  ]);

  const [
    filterConditions,
    clearFilterConditions,
    forceFilterConditions,
    setFilterConditions
  ] = useAutoReducer<EstateFilterConditions>(filterConditionsOfUser);

  const defaultCities = useMemo(
    () =>
      mainContext.company?.areas
        ?.map((city_code) =>
          cities.find((city) => city.city_code === city_code)
        )
        .filter((city) => city !== undefined) as City[],
    [cities, mainContext.company?.areas]
  );

  const defaultSelectableCities = useMemo(
    () =>
      mainContext.company?.eAreas
        ?.map((city_code) =>
          cities.find((city) => city.city_code === city_code)
        )
        .filter((city) => city !== undefined) as City[],
    [cities, mainContext.company?.eAreas]
  );

  const selectableCities = useMemo(
    () =>
      ((defaultSelectableCities?.length ?? 0) !== 0
        ? defaultSelectableCities
        : cities.filter((city) => {
            const specialCityCodes = specialCities
              .filter((specialCity) => specialCity.pref_code === city.pref_code)
              .map((x) => x.city_code);

            return (
              specialCityCodes.includes(city.city_code) ||
              specialCityCodes.length === 0
            );
          })
      ).map((city) => {
        const specialCity = specialCities.find(
          (x) => x.city_code === city.city_code
        );

        return {
          ...city,
          area_code: specialCity?.area_code,
          area_name: specialCity?.area_name
        };
      }),
    [cities, defaultSelectableCities]
  );

  const selectedCityCodes = useMemo(
    () => selectedCities.map((x) => x.city_code),
    [selectedCities]
  );

  const formattedEstatePriceFrom = useMemo(
    () =>
      filterConditions.estatePriceFrom === estatePriceMin
        ? "下限なし"
        : filterConditions.estatePriceFrom.toLocaleString(),
    [filterConditions.estatePriceFrom]
  );

  const formattedEstatePriceTo = useMemo(
    () =>
      filterConditions.estatePriceTo === estatePriceMax
        ? "上限なし"
        : filterConditions.estatePriceTo.toLocaleString(),
    [filterConditions.estatePriceTo]
  );

  const { tsuboAreaFromIndex, tsuboAreaToIndex } = useMemo(
    () =>
      ({
        tsuboAreaFromIndex: tsuboAreaMarks.findIndex(
          (x) => x.value === filterConditions.tsuboAreaFrom
        ),
        tsuboAreaToIndex: tsuboAreaMarks.findIndex(
          (x) => x.value === filterConditions.tsuboAreaTo
        )
      }) as const,
    [filterConditions.tsuboAreaFrom, filterConditions.tsuboAreaTo]
  );

  const formattedTsuboAreaFrom = useMemo(
    () =>
      filterConditions.tsuboAreaFrom === tsuboAreaMin
        ? "下限なし"
        : filterConditions.tsuboAreaFrom.toLocaleString(),
    [filterConditions.tsuboAreaFrom]
  );

  const formattedTsuboAreaTo = useMemo(
    () =>
      filterConditions.tsuboAreaTo === tsuboAreaMax
        ? "上限なし"
        : filterConditions.tsuboAreaTo.toLocaleString(),
    [filterConditions.tsuboAreaTo]
  );

  const { squareAreaOfMansionFromIndex, squareAreaOfMansionToIndex } = useMemo(
    () =>
      ({
        squareAreaOfMansionFromIndex: squareAreaOfMansionMarks.findIndex(
          (x) => x.value === filterConditions.squareAreaOfMansionFrom
        ),
        squareAreaOfMansionToIndex: squareAreaOfMansionMarks.findIndex(
          (x) => x.value === filterConditions.squareAreaOfMansionTo
        )
      }) as const,
    [
      filterConditions.squareAreaOfMansionFrom,
      filterConditions.squareAreaOfMansionTo
    ]
  );

  const formattedSquareAreaOfMansionFrom = useMemo(
    () =>
      filterConditions.squareAreaOfMansionFrom === squareAreaOfMansionMin
        ? "下限なし"
        : filterConditions.squareAreaOfMansionFrom.toLocaleString(),
    [filterConditions.squareAreaOfMansionFrom]
  );

  const formattedSquareAreaOfMansionTo = useMemo(
    () =>
      filterConditions.squareAreaOfMansionTo === squareAreaOfMansionMax
        ? "上限なし"
        : filterConditions.squareAreaOfMansionTo.toLocaleString(),
    [filterConditions.squareAreaOfMansionTo]
  );

  const { completionYMFromIndex, completionYMToIndex } = useMemo(
    () =>
      ({
        completionYMFromIndex: completionYMs.findIndex(
          (x) => x === filterConditions.completionYMTo
        ),
        completionYMToIndex: completionYMs.findIndex(
          (x) => x === filterConditions.completionYMFrom
        )
      }) as const,
    [filterConditions.completionYMFrom, filterConditions.completionYMTo]
  );

  const formattedCompletionYMFrom = useMemo(
    () =>
      completionYMFromIndex === 0
        ? "新築"
        : (completionYMMarks.at(completionYMFromIndex)?.label ?? ""),
    [completionYMFromIndex]
  );

  const formattedCompletionYMTo = useMemo(
    () =>
      completionYMToIndex === completionYMs.length - 1
        ? "築年指定なし"
        : (completionYMMarks.at(completionYMToIndex)?.label ?? ""),
    [completionYMToIndex]
  );

  const { roomsFromIndex, roomsToIndex } = useMemo(
    () =>
      ({
        roomsFromIndex: roomsMarks.findIndex(
          (x) => x.value === filterConditions.roomsFrom
        ),
        roomsToIndex: roomsMarks.findIndex(
          (x) => x.value === filterConditions.roomsTo
        )
      }) as const,
    [filterConditions.roomsFrom, filterConditions.roomsTo]
  );

  const formattedRoomsFrom = useMemo(
    () =>
      filterConditions.roomsFrom === roomsMin
        ? "下限なし"
        : filterConditions.roomsFrom.toLocaleString(),
    [filterConditions.roomsFrom]
  );

  const formattedRoomsTo = useMemo(
    () =>
      filterConditions.roomsTo === roomsMax
        ? "上限なし"
        : filterConditions.roomsTo.toLocaleString(),
    [filterConditions.roomsTo]
  );

  const resetButtonOn = useMemo(
    () =>
      (selectedCities.length === defaultCities.length &&
        selectedCities
          .map((x) => x.city_code)
          .every((x) => defaultCities.map((x) => x.city_code).includes(x))) ===
        false ||
      (filterConditions.estateTypes.length ===
        props.defaultFilterConditions.estateTypes.length &&
        props.defaultFilterConditions.estateTypes
          .map((x) => x.id)
          .every((x) =>
            filterConditions.estateTypes.map((x) => x.id).includes(x)
          )) === false ||
      filterConditions.estatePriceFrom !== estatePriceMin ||
      filterConditions.estatePriceTo !== estatePriceMax ||
      filterConditions.tsuboAreaFrom !== tsuboAreaMin ||
      filterConditions.tsuboAreaTo !== tsuboAreaMax ||
      filterConditions.squareAreaOfMansionFrom !== squareAreaOfMansionMin ||
      filterConditions.squareAreaOfMansionTo !== squareAreaOfMansionMax ||
      filterConditions.completionYMFrom !==
        completionYMs[completionYMs.length - 1] ||
      filterConditions.completionYMTo !== completionYMs[0] ||
      (filterConditions.bldConditionSelections.length ===
        props.defaultFilterConditions.bldConditionSelections.length &&
        props.defaultFilterConditions.bldConditionSelections
          .map((x) => x.id)
          .every((x) =>
            filterConditions.bldConditionSelections.map((x) => x.id).includes(x)
          )) === false ||
      filterConditions.petOk !== false ||
      filterConditions.flat !== false ||
      filterConditions.forRent !== false ||
      filterConditions.exceptForRent !== false ||
      filterConditions.companyRecNo !== 0 ||
      filterConditions.venderOnly !== false ||
      filterConditions.structureSummaries.length !==
        structureSummaries.length ||
      filterConditions.purposeSummaryBs.length !== purposeSummaryBs.length ||
      filterConditions.alsoCreatedAtFrom !== dateMin ||
      filterConditions.alsoCreatedAtTo !== dateMax ||
      filterConditions.alsoPricedAtFrom !== dateMin ||
      filterConditions.alsoPricedAtTo !== dateMax ||
      filterConditions.roomsFrom !== roomsMin ||
      filterConditions.roomsTo !== roomsMax,
    [
      defaultCities,
      filterConditions.alsoCreatedAtFrom,
      filterConditions.alsoCreatedAtTo,
      filterConditions.alsoPricedAtFrom,
      filterConditions.alsoPricedAtTo,
      filterConditions.bldConditionSelections,
      filterConditions.companyRecNo,
      filterConditions.completionYMFrom,
      filterConditions.completionYMTo,
      filterConditions.estatePriceFrom,
      filterConditions.estatePriceTo,
      filterConditions.estateTypes,
      filterConditions.exceptForRent,
      filterConditions.flat,
      filterConditions.forRent,
      filterConditions.petOk,
      filterConditions.venderOnly,
      filterConditions.purposeSummaryBs.length,
      filterConditions.roomsFrom,
      filterConditions.roomsTo,
      filterConditions.squareAreaOfMansionFrom,
      filterConditions.squareAreaOfMansionTo,
      filterConditions.structureSummaries.length,
      filterConditions.tsuboAreaFrom,
      filterConditions.tsuboAreaTo,
      props.defaultFilterConditions.bldConditionSelections,
      props.defaultFilterConditions.estateTypes,
      purposeSummaryBs.length,
      selectedCities,
      structureSummaries.length
    ]
  );

  const okButtonOn = useMemo(() => selectedCities.length > 0, [selectedCities]);

  const isAdministrator = useMemo(() => {
    return (mainContext.groups || []).includes(GroupEnum.Administrator);
  }, [mainContext.groups]);

  const isPricingAvailable = useMemo(() => {
    return (
      (mainContext.groups || []).includes(GroupEnum.Staff) ||
      (mainContext.user?.pricingAvailable ?? false)
    );
  }, [mainContext.groups, mainContext.user?.pricingAvailable]);

  const onCitiesClick = useCallback(
    (city: City) => {
      const newSelectedCities = [...selectedCities];
      const foundIndex = newSelectedCities.findIndex(
        (x) => x.city_code === city.city_code
      );

      if (foundIndex > -1) {
        newSelectedCities.splice(foundIndex, 1);
      } else {
        newSelectedCities.push(city);
      }

      setSelectedCities(newSelectedCities);
    },
    [selectedCities]
  );

  const onEstateTypesChange = useCallback(
    (values: EstateType[]) => {
      setFilterConditions.estateTypes(values);
    },
    [setFilterConditions]
  );

  const onPriceLabelChange = useCallback((value: boolean) => {
    setPriceLabel(value);
  }, []);

  const onTsuboLabelChange = useCallback((value: boolean) => {
    setTsuboLabel(value);
  }, []);

  const onEstatePriceChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const estatePrice = value as number[];
      setFilterConditions.estatePriceFrom(estatePrice[0]);
      setFilterConditions.estatePriceTo(estatePrice[1]);
    },
    [setFilterConditions]
  );

  const onTsuboAreaChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const tsuboAreaIndices = value as number[];

      setFilterConditions.tsuboAreaFrom(
        tsuboAreaMarks[tsuboAreaIndices[0]].value
      );
      setFilterConditions.tsuboAreaTo(
        tsuboAreaMarks[tsuboAreaIndices[1]].value
      );
    },
    [setFilterConditions]
  );

  const onSquareAreaOfMansionChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const squareAreaOfMansionIndices = value as number[];

      setFilterConditions.squareAreaOfMansionFrom(
        squareAreaOfMansionMarks[squareAreaOfMansionIndices[0]].value
      );
      setFilterConditions.squareAreaOfMansionTo(
        squareAreaOfMansionMarks[squareAreaOfMansionIndices[1]].value
      );
    },
    [setFilterConditions]
  );

  const onCompletionYMIndexChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const completionYMIndices = value as number[];

      setFilterConditions.completionYMFrom(
        completionYMs[completionYMIndices[1]]
      );
      setFilterConditions.completionYMTo(completionYMs[completionYMIndices[0]]);
    },
    [setFilterConditions]
  );

  const onRoomsChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const roomsIndices = value as number[];

      setFilterConditions.roomsFrom(roomsMarks[roomsIndices[0]].value);
      setFilterConditions.roomsTo(roomsMarks[roomsIndices[1]].value);
    },
    [setFilterConditions]
  );

  const onBldConditionSelectionsChange = useCallback(
    (values: BldConditionSelection[]) => {
      setFilterConditions.bldConditionSelections(values);
    },
    [setFilterConditions]
  );

  const onPetOkChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.petOk(checked);
    },
    [setFilterConditions]
  );

  const onFlatChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.flat(checked);
    },
    [setFilterConditions]
  );

  const onForRentChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.forRent(checked);

      if (checked) {
        setFilterConditions.exceptForRent(false);
      }
    },
    [setFilterConditions]
  );

  const onExceptForRentChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.exceptForRent(checked);

      if (checked) {
        setFilterConditions.forRent(false);
      }
    },
    [setFilterConditions]
  );

  const onMyCompanyOnlyChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.companyRecNo(
        checked && mainContext.company?.recNo ? mainContext.company.recNo : 0
      );
    },
    [mainContext, setFilterConditions]
  );

  const onVenderOnlyChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.venderOnly(checked);
    },
    [setFilterConditions]
  );

  const onStructureSummariesChange: (
    structureSummaries: StructureSummary[]
  ) => void = useCallback(
    (structureSummaries) => {
      setFilterConditions.structureSummaries(structureSummaries);
    },
    [setFilterConditions]
  );

  const onPurposeSummaryBsChange: (
    purposeSummaryBs: PurposeSummaryB[]
  ) => void = useCallback(
    (purposeSummaryBs) => {
      setFilterConditions.purposeSummaryBs(purposeSummaryBs);
    },
    [setFilterConditions]
  );

  const onAlsoCreatedAtFromChange = useCallback(
    (value: number | null | undefined) => {
      setFilterConditions.alsoCreatedAtFrom(value ?? dateMin);
    },
    [setFilterConditions]
  );

  const onAlsoCreatedAtToChange = useCallback(
    (value: number | null | undefined) => {
      const newValue =
        value === null || value === undefined
          ? dateMax
          : new Date(new Date(value).setHours(23, 59, 59, 998)).getTime();

      setFilterConditions.alsoCreatedAtTo(newValue);
    },
    [setFilterConditions]
  );

  const onAlsoPricedAtFromChange = useCallback(
    (value: number | null | undefined) => {
      setFilterConditions.alsoPricedAtFrom(value ?? dateMin);
    },
    [setFilterConditions]
  );

  const onAlsoPricedAtToChange = useCallback(
    (value: number | null | undefined) => {
      const newValue =
        value === null || value === undefined
          ? dateMax
          : new Date(new Date(value).setHours(23, 59, 59, 998)).getTime();

      setFilterConditions.alsoPricedAtTo(newValue);
    },
    [setFilterConditions]
  );

  const onResetButtonClick = useCallback(() => {
    setSelectedCities(defaultCities);

    forceFilterConditions({
      ...filterConditionsOfUser,
      estateTypes: props.defaultFilterConditions.estateTypes,
      estatePriceFrom: estatePriceMin,
      estatePriceTo: estatePriceMax,
      tsuboAreaFrom: tsuboAreaMin,
      tsuboAreaTo: tsuboAreaMax,
      squareAreaOfMansionFrom: squareAreaOfMansionMin,
      squareAreaOfMansionTo: squareAreaOfMansionMax,
      completionYMFrom: completionYMs[completionYMs.length - 1],
      completionYMTo: completionYMs[0],
      bldConditionSelections:
        props.defaultFilterConditions.bldConditionSelections,
      petOk: false,
      flat: false,
      forRent: false,
      exceptForRent: false,
      companyRecNo: 0,
      structureSummaries: structureSummaries,
      purposeSummaryBs: purposeSummaryBs,
      alsoCreatedAtFrom: dateMin,
      alsoCreatedAtTo: dateMax,
      alsoPricedAtFrom: dateMin,
      alsoPricedAtTo: dateMax,
      roomsFrom: roomsMin,
      roomsTo: roomsMax
    });
  }, [
    defaultCities,
    forceFilterConditions,
    filterConditionsOfUser,
    props.defaultFilterConditions.estateTypes,
    props.defaultFilterConditions.bldConditionSelections,
    structureSummaries,
    purposeSummaryBs
  ]);

  const buildDefaultCondition = useCallback((): DefaultCondition => {
    const defaultCondition: DefaultCondition =
      buildFilterConditionsOfDefaultCondition(filterConditions, {
        estateTypes: props.defaultFilterConditions.estateTypes,
        bldConditionSelections:
          props.defaultFilterConditions.bldConditionSelections,
        structureSummaries: structureSummaries,
        purposeSummaryBs: purposeSummaryBs
      });

    if (
      (selectedCities.length === defaultCities.length &&
        selectedCities
          .map((x) => x.city_code)
          .every((x) => defaultCities.map((x) => x.city_code).includes(x))) ===
      false
    ) {
      defaultCondition.areaPrefs = Array.from(
        new Set(selectedPrefectures.map((x) => x.pref_code))
      );

      defaultCondition.areas = Array.from(
        new Set(selectedCities.map((x) => x.city_code))
      );
    }

    if (priceLabel !== defaultDspPrice) {
      defaultCondition.priceLabel = priceLabel;
    }

    if (tsuboLabel !== defaultDspTsuboArea) {
      defaultCondition.tsuboLabel = tsuboLabel;
    }

    return defaultCondition;
  }, [
    defaultCities,
    filterConditions,
    priceLabel,
    props.defaultFilterConditions.bldConditionSelections,
    props.defaultFilterConditions.estateTypes,
    purposeSummaryBs,
    selectedCities,
    selectedPrefectures,
    structureSummaries,
    tsuboLabel
  ]);

  const saveDefaultCondition = useCallback(async () => {
    try {
      setBusy(true);

      const newDefaultCondition = buildDefaultCondition();

      const saved = await saveDefaultConditionOfUser(newDefaultCondition);

      if (saved === null || saved === undefined) {
        return false;
      }

      mainContext.setDefaultConditionOfUser(newDefaultCondition);

      return true;
    } finally {
      setBusy(false);
    }
  }, [buildDefaultCondition, mainContext, saveDefaultConditionOfUser]);

  const onOkButtonClick = useCallback(async () => {
    const saved = await saveDefaultCondition();

    if (!saved) {
      return;
    }

    props.onOkButtonClick({ areaChanged: false, priceLabel, tsuboLabel });

    if (props.onClose) {
      props.onClose();
    }
  }, [props, saveDefaultCondition, priceLabel, tsuboLabel]);

  const onOkAndExecuteButtonClick = useCallback(async () => {
    if (okButtonOn === false) {
      return;
    }

    const saved = await saveDefaultCondition();

    if (!saved) {
      return;
    }

    const areaChanged =
      selectedCitiesOfUser.length !== selectedCities.length ||
      selectedCitiesOfUser
        .map((x) => x.city_code)
        .some((x) => !selectedCityCodes.includes(x)) ||
      selectedCities
        .map((x) => x.city_code)
        .some((x) => !selectedCitiesOfUser.map((y) => y.city_code).includes(x));

    props.onOkButtonClick({
      selectedPrefectures,
      selectedCities,
      filterConditions,
      areaChanged,
      priceLabel,
      tsuboLabel
    });

    if (props.onClose) {
      props.onClose();
    }
  }, [
    okButtonOn,
    saveDefaultCondition,
    selectedCitiesOfUser,
    selectedCities,
    props,
    selectedPrefectures,
    filterConditions,
    priceLabel,
    tsuboLabel,
    selectedCityCodes
  ]);

  const onBackPaperClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (titRef.current && boxRef.current) {
        if (
          titRef.current.contains(event.target as Node) ||
          boxRef.current.contains(event.target as Node)
        ) {
          return;
        }

        if (props.onClose) {
          props.onClose();
        }
      }
    },
    [props]
  );

  useEffect(() => {
    if (initRef.current) {
      initRef.current = false;
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCitiesErrorMessage("");

    if (!props.open) {
      setSelectedCities([]);
      clearFilterConditions();
      setPriceLabel(defaultDspPrice);
      setTsuboLabel(defaultDspTsuboArea);
    }
  }, [clearFilterConditions, props.open]);

  useEffect(() => {
    if (props.open && selectedPrefecturesOfUser.length !== 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedCities(selectedCitiesOfUser);
    } else if (props.open) {
      setSelectedCities(defaultCities);
    } else {
      setSelectedCities([]);
    }
  }, [
    defaultCities,
    props.open,
    selectedCitiesOfUser,
    selectedPrefecturesOfUser.length
  ]);

  useEffect(() => {
    forceFilterConditions(filterConditionsOfUser);
  }, [forceFilterConditions, filterConditionsOfUser, props.open]);

  useEffect(() => {
    if (props.open) {
      if (
        mainContext.defaultConditionOfUser?.priceLabel !== null &&
        mainContext.defaultConditionOfUser?.priceLabel !== undefined
      ) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPriceLabel(mainContext.defaultConditionOfUser.priceLabel);
      } else {
        setPriceLabel(defaultDspPrice);
      }

      if (
        mainContext.defaultConditionOfUser?.tsuboLabel !== null &&
        mainContext.defaultConditionOfUser?.tsuboLabel !== undefined
      ) {
        setTsuboLabel(mainContext.defaultConditionOfUser.tsuboLabel);
      } else {
        setTsuboLabel(defaultDspTsuboArea);
      }
    }
  }, [
    mainContext.defaultConditionOfUser?.priceLabel,
    mainContext.defaultConditionOfUser?.tsuboLabel,
    props.open
  ]);

  return {
    sliderTheme,

    titRef,
    boxRef,

    busy: busy || busyOfUser,

    selectedPrefectures,
    selectedCities,
    citiesErrorMessage,
    selectableCities,
    selectedCityCodes,

    filterConditions,
    estateTypes,
    bldConditionSelections,
    structureSummaries,
    purposeSummaryBs,

    formattedEstatePriceFrom,
    formattedEstatePriceTo,

    formattedTsuboAreaFrom,
    formattedTsuboAreaTo,

    tsuboAreaFromIndex,
    tsuboAreaToIndex,

    formattedSquareAreaOfMansionFrom,
    formattedSquareAreaOfMansionTo,

    squareAreaOfMansionFromIndex,
    squareAreaOfMansionToIndex,

    formattedCompletionYMFrom,
    formattedCompletionYMTo,

    completionYMFromIndex,
    completionYMToIndex,

    formattedRoomsFrom,
    formattedRoomsTo,

    roomsFromIndex,
    roomsToIndex,

    priceLabel,
    tsuboLabel,

    resetButtonOn,
    okButtonOn,

    isAdministrator,
    isPricingAvailable,

    onCitiesClick,

    onEstateTypesChange,

    onEstatePriceChange,

    onPriceLabelChange,
    onTsuboLabelChange,

    onTsuboAreaChange,
    onSquareAreaOfMansionChange,

    onCompletionYMIndexChange,

    onRoomsChange,

    onBldConditionSelectionsChange,

    onPetOkChange,
    onFlatChange,
    onForRentChange,
    onExceptForRentChange,
    onMyCompanyOnlyChange,
    onVenderOnlyChange,
    onStructureSummariesChange,
    onPurposeSummaryBsChange,

    onAlsoCreatedAtFromChange,
    onAlsoCreatedAtToChange,
    onAlsoPricedAtFromChange,
    onAlsoPricedAtToChange,

    onResetButtonClick,
    onOkButtonClick,
    onOkAndExecuteButtonClick,
    onBackPaperClick
  } as const;
};

export type UseDefaultConditionInputDialogHooksType = ReturnType<
  typeof useDefaultConditionInputDialogHooks
>;
